export const PROJECT_GEOMETRY_DEFAULT_COLOR = '#0ea5e9';
export const PROJECT_GEOMETRY_INVALID_COLOR = '#94a3b8';

export const PROJECT_GEOMETRY_COLOR_PRESETS = [
  { name: 'Sky blue', color: '#38bdf8' },
  { name: 'Blue', color: '#3b82f6' },
  { name: 'Cyan', color: '#06b6d4' },
  { name: 'Teal', color: '#14b8a6' },
  { name: 'Green', color: '#22c55e' },
  { name: 'White', color: '#e2e8f0' },
] as const;

const parseHexColor = (color?: string): [number, number, number] | null => {
  const raw = color?.trim().replace(/^#/, '');
  if (!raw) return null;

  const normalized = raw.length === 3
    ? raw.split('').map((channel) => `${channel}${channel}`).join('')
    : raw.slice(0, 6);
  if (normalized.length !== 6 || !/^[0-9a-f]{6}$/i.test(normalized)) {
    return null;
  }

  return [
    parseInt(normalized.slice(0, 2), 16),
    parseInt(normalized.slice(2, 4), 16),
    parseInt(normalized.slice(4, 6), 16),
  ];
};

const getHueAndSaturation = ([red, green, blue]: [number, number, number]) => {
  const r = red / 255;
  const g = green / 255;
  const b = blue / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  const lightness = (max + min) / 2;
  const saturation = delta === 0
    ? 0
    : delta / (1 - Math.abs(2 * lightness - 1));

  if (delta === 0) {
    return { hue: 0, saturation };
  }

  const hueSegment = max === r
    ? ((g - b) / delta) % 6
    : max === g
      ? (b - r) / delta + 2
      : (r - g) / delta + 4;
  const hue = (hueSegment * 60 + 360) % 360;
  return { hue, saturation };
};

export const normalizeProjectGeometryColor = (
  color?: string,
  fallback = PROJECT_GEOMETRY_DEFAULT_COLOR
): string => {
  const rgb = parseHexColor(color);
  if (!rgb) return fallback;

  const { hue, saturation } = getHueAndSaturation(rgb);
  const isNeutral = saturation <= 0.12;
  const isBlueGreen = hue >= 75 && hue <= 225;
  if (!isNeutral && !isBlueGreen) return fallback;
  // Canonical #rrggbb — inputs may arrive as "0ea5e9" or "#0af", which native
  // color inputs and OpenLayers reject.
  return `#${rgb.map((channel) => channel.toString(16).padStart(2, '0')).join('')}`;
};
